import React, { useContext, lazy, Suspense, useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Drawer, AppBar, Toolbar, Typography, List, ListItem, ListItemIcon, ListItemText, Box, IconButton,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon, People, LocalHospital, Payment, Settings, Assessment, Security, Logout,
  MoneyOff, AccountBalance, Menu as MenuIcon,
} from '@mui/icons-material';
import Dashboard from './components/Dashboard';
import DoctorManagement from './components/DoctorManagement';
import PatientManagement from './components/PatientManagement';
import AppointmentManagement from './components/AppointmentManagement';
import PaymentManagement from './components/PaymentManagement';
import WithdrawalRequestsHistory from './components/WithdrawalRequestsHistory';
import DoctorPaymentDetailPage from './components/DoctorPaymentDetailPage';
import PlatformSettings from './components/PlatformSettings';
import ReportsAnalytics from './components/ReportsAnalytics';
import DoctorDetails from './components/DoctorDetails';
import PatientDetails from './components/PatientDetails';
import AdminManagement from './components/AdminManagement';
import AdminLogin from './AdminLogin';
import FinancialReport from './components/FinancialReport';
import logo from '../assets/logo.png';

const RefundManagement = lazy(() => import('./components/RefundManagement'));

const AdminPage = () => {
  const { adminUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    console.log('AdminPage rendered, adminUser:', adminUser);
    if (!adminUser && window.location.pathname !== '/admin') {
      console.log('No adminUser, redirecting to /admin');
      navigate('/admin', { replace: true });
    } else if (adminUser && window.location.pathname === '/admin') {
      const defaultPath =
        adminUser.role === 'Operations Manager' || adminUser.role === 'Doctor Manager'
          ? '/admin/doctors'
          : adminUser.role === 'Finance Manager'
          ? '/admin/payments'
          : adminUser.role === 'Support Staff'
          ? '/admin/patients'
          : '/admin/dashboard';
      console.log(`Admin logged in, redirecting to ${defaultPath}`);
      navigate(defaultPath, { replace: true });
    }
    if (isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [adminUser, navigate, isMobile]);

  const handleLogout = () => {
    logout('admin');
    navigate('/admin', { replace: true });
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  if (!adminUser) {
    console.log('AdminUser is falsy, rendering AdminLogin');
    return <AdminLogin />;
  }

  const rolePermissions = {
    'Super Admin': [
      'Dashboard', 'Doctors', 'Patients', 'Appointments', 'Payments', 'Refunds',
      'Financial Report', 'Settings', 'Reports', 'Admins'
    ],
    'Operations Manager': ['Doctors', 'Patients', 'Appointments', 'Refunds'],
    'Doctor Manager': ['Doctors', 'Appointments'],
    'Finance Manager': ['Payments', 'Refunds', 'Financial Report'],
    'Support Staff': ['Patients', 'Refunds', 'Appointments'],
  };

  const allMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
    { text: 'Doctors', icon: <LocalHospital />, path: '/admin/doctors' },
    { text: 'Patients', icon: <People />, path: '/admin/patients' },
    { text: 'Appointments', icon: <DashboardIcon />, path: '/admin/appointments' },
    { text: 'Payments', icon: <Payment />, path: '/admin/payments' },
    { text: 'Refunds', icon: <MoneyOff />, path: '/admin/refunds' },
    { text: 'Financial Report', icon: <AccountBalance />, path: '/admin/financial-report' },
    { text: 'Settings', icon: <Settings />, path: '/admin/settings' },
    { text: 'Reports', icon: <Assessment />, path: '/admin/reports' },
    { text: 'Admins', icon: <Security />, path: '/admin/admins' },
  ];

  const allowedMenuItems = allMenuItems.filter((item) =>
    rolePermissions[adminUser.role]?.includes(item.text)
  );

  const drawerContent = (
    <Box
      sx={{
        background: '#1976D2',
        color: '#fff',
        height: '100%',
        width: isMobile ? '70vw' : 240,
        maxWidth: 240,
        transition: 'width 0.3s ease',
      }}
    >
      <Toolbar />
      <List>
        {allowedMenuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            component={Link}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon sx={{ color: '#fff', minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{ fontSize: isMobile ? '1rem' : '0.875rem' }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: 1201,
          background: '#1976d2',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <img
            src={logo}
            alt="TureMedicine"
            style={{ height: isMobile ? 32 : 40, marginRight: 16 }}
          />
          <Typography
            variant="h6"
            flexGrow={1}
            sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
          >
            TureMedicine Admin - {adminUser.role}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: 240,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: 240,
            background: '#1976D2',
            color: '#fff',
            boxSizing: 'border-box',
            borderRight: 'none',
            boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: '70vw',
            maxWidth: 240,
            background: '#1976D2',
            color: '#fff',
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          mt: 8,
          width: { xs: '100%', md: 'calc(100% - 240px)' },
          bgcolor: '#f5f5f5',
          overflowY: 'auto',
        }}
      >
        <Suspense fallback={<Typography>Loading...</Typography>}>
          <Routes>
            {rolePermissions[adminUser.role]?.includes('Dashboard') && (
              <Route path="dashboard" element={<Dashboard />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Doctors') && (
              <Route path="doctors" element={<DoctorManagement />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Doctors') && (
              <Route path="doctors/:id" element={<DoctorDetails />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Patients') && (
              <Route path="patients" element={<PatientManagement />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Doctors') && (
              <Route path="doctor-payment-details/:doctorId" element={<DoctorPaymentDetailPage />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Patients') && (
              <Route path="patients/:id" element={<PatientDetails />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Appointments') && (
              <Route path="appointments" element={<AppointmentManagement />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Payments') && (
              <Route path="payments" element={<PaymentManagement />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Payments') && (
              <Route path="withdrawal-requests-history" element={<WithdrawalRequestsHistory />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Refunds') && (
              <Route path="refunds" element={<RefundManagement />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Financial Report') && (
              <Route path="financial-report" element={<FinancialReport />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Settings') && (
              <Route path="settings" element={<PlatformSettings />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Reports') && (
              <Route path="reports" element={<ReportsAnalytics />} />
            )}
            {rolePermissions[adminUser.role]?.includes('Admins') && (
              <Route path="admins" element={<AdminManagement />} />
            )}
            <Route path="*" element={<Typography>404 - Access Denied or Page Not Found</Typography>} />
          </Routes>
        </Suspense>
      </Box>
    </Box>
  );
};

export default AdminPage;